import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { growMotion } from '@/src/renderer/lib/motion/growMotion'
import { TaskStartIcon, TaskDeadlineIcon, TaskAttentionIcon, DurationIcon, TagIcon, RecurringIcon } from '@/src/renderer/components/icons'
import { TaskActionButton } from '../TaskActionButton'
import { TaskOptions } from '../TaskOptions'
import { AttentionDropdownContent } from '../AttentionDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { TagDropdownContent } from '../TagDropdown'
import { WhenDropdownContent } from '../WhenDropdown'
import { DeadlineDropdownContent } from '../DeadlineDropdown'
import { RecurrenceDropdownContent } from '../RecurrenceDropdown'
import type { StartValue } from '../WhenDropdown'
import type { DeadlineValue } from '../DeadlineDropdown'
import type { AttentionLevel, RecurrenceRule } from '@/src/renderer/lib/db/models'
import type { DurationValue } from '../TaskEntry'
import type { TaskActionBarProps } from './TaskActionBar.types'
import './TaskActionBar.scss'

/*
  Ein Knopf, der in der Reihe Platz macht und sich dann füllt — die Staffelung
  steckt in `growMotion` (`lib/motion/growMotion.ts`), samt Begründung. Diese Zeile
  war der Ursprung des Musters; drei weitere Stellen haben es kopiert, bevor es
  extrahiert wurde.
*/
const {
  initial: actionBtnInitial,
  animate: actionBtnEnter,
  exit: actionBtnExit,
  style: actionBtnStyle,
} = growMotion({ axis: 'width', size: 32 })

export const TaskActionBar: React.FC<TaskActionBarProps> = ({
  start = null,
  onStartChange,
  deadline = null,
  onDeadlineChange,
  attention = null,
  onAttentionChange,
  duration = null,
  onDurationChange,
  tags = [],
  onTagsChange,
  recurrence = null,
  onRecurrenceChange,
  seedDate = null,
  taskTitle = '',
  showOptions = true,
}) => {
  const { t } = useTranslation()
  const [startOpen, setStartOpen] = useState(false)
  const [deadlineOpen, setDeadlineOpen] = useState(false)
  const [attentionOpen, setAttentionOpen] = useState(false)
  const [durationOpen, setDurationOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(false)
  const [recurrenceOpen, setRecurrenceOpen] = useState(false)
  const anyOpen = startOpen || deadlineOpen || attentionOpen || durationOpen || tagsOpen || recurrenceOpen
  // Track whether a dropdown opened via hover-switch (skip fade-in animation).
  // Reset when all dropdowns close so the next click-open gets the animation.
  const [switchedOpen, setSwitchedOpen] = useState(false)
  useEffect(() => {
    if (!anyOpen && switchedOpen) setSwitchedOpen(false)
  }, [anyOpen, switchedOpen])

  // Button order: Start=0, Deadline=1, Duration=2, Recurrence=3, Attention=4, Tags=5 —
  // dieselbe Reihenfolge wie im Aufgaben-Menü und in den Aufgaben-Details: Ende folgt immer auf Start (Sven, 05.10.2026).
  const openButtonIndex = startOpen ? 0 : deadlineOpen ? 1 : durationOpen ? 2 : recurrenceOpen ? 3 : attentionOpen ? 4 : tagsOpen ? 5 : -1

  // Block hover-switch only in the direction of a sideways-flipped dropdown.
  // E.g. dropdown flipped left → block switching to buttons LEFT of the trigger,
  // but allow switching to buttons RIGHT (away from the dropdown).
  const shouldBlockSwitch = useCallback((hoveredIndex: number) => {
    if (openButtonIndex < 0) return false
    const dropdown = document.querySelector('[data-task-dropdown]')
    const placement = dropdown?.getAttribute('data-placement') ?? ''
    if (placement.startsWith('left') && hoveredIndex < openButtonIndex) return true
    if (placement.startsWith('right') && hoveredIndex > openButtonIndex) return true
    return false
  }, [openButtonIndex])

  // Delayed hover-switch: only switch when mouse dwells on a button (not passing through)
  const SWITCH_DWELL_DELAY = 100
  const switchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearSwitchTimer = useCallback(() => {
    if (switchTimerRef.current) { clearTimeout(switchTimerRef.current); switchTimerRef.current = null }
  }, [])
  const delayedSwitch = useCallback((fn: () => void) => {
    clearSwitchTimer()
    switchTimerRef.current = setTimeout(() => { switchTimerRef.current = null; setSwitchedOpen(true); fn() }, SWITCH_DWELL_DELAY)
  }, [clearSwitchTimer])

  const handleStartChange = useCallback((value: StartValue) => {
    setStartOpen(false)
    setTimeout(() => {
      onStartChange?.(value)
    }, 0)
  }, [onStartChange])

  const handleStartClose = useCallback(() => {
    setStartOpen(false)
  }, [])

  const handleStartMouseEnter = useCallback(() => {
    if (anyOpen && !startOpen && !shouldBlockSwitch(0)) { clearSwitchTimer(); delayedSwitch(() => { setDeadlineOpen(false); setAttentionOpen(false); setDurationOpen(false); setTagsOpen(false); setRecurrenceOpen(false); setStartOpen(true) }) }
  }, [anyOpen, startOpen, shouldBlockSwitch, delayedSwitch, clearSwitchTimer])

  const handleDeadlineChange = useCallback((value: DeadlineValue) => {
    setDeadlineOpen(false)
    setTimeout(() => {
      onDeadlineChange?.(value)
    }, 0)
  }, [onDeadlineChange])

  const handleDeadlineClose = useCallback(() => {
    setDeadlineOpen(false)
  }, [])

  const handleDeadlineMouseEnter = useCallback(() => {
    if (anyOpen && !deadlineOpen && !shouldBlockSwitch(1)) { clearSwitchTimer(); delayedSwitch(() => { setStartOpen(false); setAttentionOpen(false); setDurationOpen(false); setTagsOpen(false); setRecurrenceOpen(false); setDeadlineOpen(true) }) }
  }, [anyOpen, deadlineOpen, shouldBlockSwitch, delayedSwitch, clearSwitchTimer])

  const handleAttentionChange = useCallback((value: AttentionLevel) => {
    setAttentionOpen(false)
    setTimeout(() => {
      onAttentionChange?.(value)
    }, 0)
  }, [onAttentionChange])

  const handleAttentionClose = useCallback(() => {
    setAttentionOpen(false)
  }, [])

  const handleDurationChange = useCallback((value: DurationValue) => {
    setDurationOpen(false)
    setTimeout(() => {
      onDurationChange?.(value)
    }, 0)
  }, [onDurationChange])

  const handleDurationClose = useCallback(() => {
    setDurationOpen(false)
  }, [])

  const handleDurationMouseEnter = useCallback(() => {
    if (anyOpen && !durationOpen && !shouldBlockSwitch(2)) { clearSwitchTimer(); delayedSwitch(() => { setStartOpen(false); setDeadlineOpen(false); setAttentionOpen(false); setTagsOpen(false); setRecurrenceOpen(false); setDurationOpen(true) }) }
  }, [anyOpen, durationOpen, shouldBlockSwitch, delayedSwitch, clearSwitchTimer])

  const handleAttentionMouseEnter = useCallback(() => {
    if (anyOpen && !attentionOpen && !shouldBlockSwitch(4)) { clearSwitchTimer(); delayedSwitch(() => { setStartOpen(false); setDeadlineOpen(false); setDurationOpen(false); setTagsOpen(false); setRecurrenceOpen(false); setAttentionOpen(true) }) }
  }, [anyOpen, attentionOpen, shouldBlockSwitch, delayedSwitch, clearSwitchTimer])

  const handleTagsChange = useCallback((ids: string[]) => {
    setTagsOpen(false)
    setTimeout(() => { onTagsChange?.(ids) }, 0)
  }, [onTagsChange])

  const handleTagsClose = useCallback(() => {
    setTagsOpen(false)
  }, [])

  const handleTagsMouseEnter = useCallback(() => {
    if (anyOpen && !tagsOpen && !shouldBlockSwitch(5)) {
      clearSwitchTimer()
      delayedSwitch(() => {
        setStartOpen(false)
        setDeadlineOpen(false)
        setAttentionOpen(false)
        setDurationOpen(false)
        setRecurrenceOpen(false)
        setTagsOpen(true)
      })
    }
  }, [anyOpen, tagsOpen, shouldBlockSwitch, delayedSwitch, clearSwitchTimer])

  const handleRecurrenceChange = useCallback((rule: RecurrenceRule) => {
    setRecurrenceOpen(false)
    setTimeout(() => { onRecurrenceChange?.(rule) }, 0)
  }, [onRecurrenceChange])

  const handleRecurrenceClose = useCallback(() => {
    setRecurrenceOpen(false)
  }, [])

  const handleRecurrenceMouseEnter = useCallback(() => {
    if (anyOpen && !recurrenceOpen && !shouldBlockSwitch(3)) { clearSwitchTimer(); delayedSwitch(() => { setStartOpen(false); setDeadlineOpen(false); setDurationOpen(false); setAttentionOpen(false); setTagsOpen(false); setRecurrenceOpen(true) }) }
  }, [anyOpen, recurrenceOpen, shouldBlockSwitch, delayedSwitch, clearSwitchTimer])

  // ── Motivieren (reward / goal) ──
  return (
      <div className="task-action-bar">
        <AnimatePresence initial={false}>
          {!start && (
            <motion.div
              key="start-btn"
              initial={actionBtnInitial}
              animate={actionBtnEnter}
              exit={actionBtnExit}
              style={actionBtnStyle}
              onMouseEnter={handleStartMouseEnter}
              onMouseLeave={clearSwitchTimer}
            >
              <TaskActionButton
                icon={<TaskStartIcon size={16} />}
                title={t('taskCreation.actionStart')}
                dateSheet={{ title: taskTitle }}
                dropdownContent={
                  <WhenDropdownContent
                    value={start}
                    onValueChange={handleStartChange}
                    onClose={handleStartClose}
                    placement="actionbar"
                  />
                }
                dropdownOpen={startOpen}
                onDropdownOpenChange={setStartOpen}
                instant={switchedOpen}
                reserveBottom={52}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {!deadline && (
            <motion.div
              key="deadline-btn"
              initial={actionBtnInitial}
              animate={actionBtnEnter}
              exit={actionBtnExit}
              style={actionBtnStyle}
              onMouseEnter={handleDeadlineMouseEnter}
              onMouseLeave={clearSwitchTimer}
            >
              <TaskActionButton
                icon={<TaskDeadlineIcon size={16} />}
                title={t('taskCreation.actionDeadline')}
                dateSheet={{ title: taskTitle }}
                dropdownContent={
                  <DeadlineDropdownContent
                    value={deadline}
                    onValueChange={handleDeadlineChange}
                    onClose={handleDeadlineClose}
                    placement="actionbar"
                  />
                }
                dropdownOpen={deadlineOpen}
                onDropdownOpenChange={setDeadlineOpen}
                instant={switchedOpen}
                reserveBottom={52}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {!duration && (
            <motion.div
              key="duration-btn"
              initial={actionBtnInitial}
              animate={actionBtnEnter}
              exit={actionBtnExit}
              style={actionBtnStyle}
              onMouseEnter={handleDurationMouseEnter}
              onMouseLeave={clearSwitchTimer}
            >
              <TaskActionButton
                icon={<DurationIcon size={16} />}
                title={t('taskCreation.actionDuration')}
                dropdownContent={
                  <DurationDropdownContent
                    value={duration}
                    onValueChange={handleDurationChange}
                    onClose={handleDurationClose}
                    placement="actionbar"
                  />
                }
                dropdownOpen={durationOpen}
                onDropdownOpenChange={setDurationOpen}
                instant={switchedOpen}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {!recurrence && onRecurrenceChange && (
            <motion.div
              key="recurrence-btn"
              initial={actionBtnInitial}
              animate={actionBtnEnter}
              exit={actionBtnExit}
              style={actionBtnStyle}
              onMouseEnter={handleRecurrenceMouseEnter}
              onMouseLeave={clearSwitchTimer}
            >
              <TaskActionButton
                icon={<RecurringIcon size={16} />}
                title={t('taskCreation.actionRecurrence')}
                dropdownContent={
                  <RecurrenceDropdownContent
                    value={recurrence}
                    seedDate={seedDate}
                    onValueChange={handleRecurrenceChange}
                    onClose={handleRecurrenceClose}
                    placement="actionbar"
                  />
                }
                dropdownOpen={recurrenceOpen}
                onDropdownOpenChange={setRecurrenceOpen}
                instant={switchedOpen}
                reserveBottom={52}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {!attention && (
            <motion.div
              key="attention-btn"
              initial={actionBtnInitial}
              animate={actionBtnEnter}
              exit={actionBtnExit}
              style={actionBtnStyle}
              onMouseEnter={handleAttentionMouseEnter}
              onMouseLeave={clearSwitchTimer}
            >
              <TaskActionButton
                icon={<TaskAttentionIcon size={16} />}
                title={t('taskCreation.actionAttention')}
                dropdownContent={
                  <AttentionDropdownContent
                    value={attention}
                    onValueChange={handleAttentionChange}
                    onClose={handleAttentionClose}
                    placement="actionbar"
                  />
                }
                dropdownOpen={attentionOpen}
                onDropdownOpenChange={setAttentionOpen}
                instant={switchedOpen}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {tags.length === 0 && (
            <motion.div
              key="tags-btn"
              initial={actionBtnInitial}
              animate={actionBtnEnter}
              exit={actionBtnExit}
              style={actionBtnStyle}
              onMouseEnter={handleTagsMouseEnter}
              onMouseLeave={clearSwitchTimer}
            >
              <TaskActionButton
                icon={<TagIcon size={16} />}
                title={t('taskCreation.actionTags')}
                dropdownContent={
                  <TagDropdownContent
                    value={tags}
                    onValueChange={handleTagsChange}
                    onClose={handleTagsClose}
                    placement="actionbar"
                  />
                }
                dropdownOpen={tagsOpen}
                onDropdownOpenChange={setTagsOpen}
                instant={switchedOpen}
                reserveBottom={52}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {/* No gift button: the reward is set from TaskRewardTrigger in the
            expanded content area, which is visible in every state. A second
            entry point for the same action would just be noise. */}
        {showOptions && <TaskOptions />}
      </div>
  )
}
